import React from "react";
import {AlertCircle, RefreshCw} from "lucide-react";
import {Button} from "./ui/button";
import {Alert, AlertDescription, AlertTitle} from "./ui/alert";

/**
 * ErrorBoundary Component
 *
 * React error boundary that catches JavaScript errors anywhere in the child
 * component tree, logs those errors, and displays a fallback UI.
 *
 * Features:
 * - Catches and displays errors gracefully
 * - Provides retry functionality
 * - Logs errors to console for debugging
 * - User-friendly error messages
 * - Responsive design matching app theme
 *
 * @class ErrorBoundary
 * @extends {React.Component}
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorCount: 0,
        };
    }

    /**
     * Update state when an error is caught
     * @static
     * @param {Error} error - The error that was thrown
     * @returns {Object} New state
     */
    static getDerivedStateFromError(error) {
        return {hasError: true};
    }

    /**
     * Log error details and update state
     * @param {Error} error - The error that was thrown
     * @param {Object} errorInfo - React error info with componentStack
     */
    componentDidCatch(error, errorInfo) {
        // Log error details to console
        console.error("[ErrorBoundary] Caught an error:", error);
        console.error("[ErrorBoundary] Error info:", errorInfo);
        console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);

        // Update state with error details
        this.setState((prevState) => ({
            error,
            errorInfo,
            errorCount: prevState.errorCount + 1,
        }));

        // Call optional error callback if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    /**
     * Reset error boundary state and retry
     */
    handleReset = () => {
        console.log("[ErrorBoundary] Resetting error boundary");
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });

        // Call optional reset callback if provided
        if (this.props.onReset) {
            this.props.onReset();
        }
    };

    /**
     * Reload the entire page
     */
    handleReload = () => {
        console.log("[ErrorBoundary] Reloading page");
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI if provided
            if (this.props.fallback) {
                return this.props.fallback({
                    error: this.state.error,
                    errorInfo: this.state.errorInfo,
                    resetError: this.handleReset,
                });
            }

            // Default fallback UI
            return (
                <div className="min-h-screen bg-background flex items-center justify-center p-6">
                    <div className="max-w-2xl w-full">
                        <Alert variant="destructive" className="bg-surface border-red-900/50">
                            <AlertCircle className="h-5 w-5"/>
                            <AlertTitle className="text-xl font-heading tracking-wide mb-2">
                                ERRORE IMPREVISTO
                            </AlertTitle>
                            <AlertDescription className="space-y-4">
                                <p className="text-gray-light">
                                    Si è verificato un errore imprevisto. Ci scusiamo per l'inconveniente.
                                </p>

                                {/* Error message */}
                                {this.state.error && (
                                    <div className="bg-black/30 border border-gray-dark rounded p-3 mt-3">
                                        <p className="text-sm font-mono text-red-400">
                                            {this.state.error.toString()}
                                        </p>
                                    </div>
                                )}

                                {/* Error count warning */}
                                {this.state.errorCount > 2 && (
                                    <div className="bg-yellow-900/20 border border-yellow-700/50 rounded p-3 mt-3">
                                        <p className="text-sm text-yellow-200">
                                            ⚠️ Questo errore si è verificato {this.state.errorCount} volte.
                                            Potrebbe essere necessario ricaricare completamente la pagina.
                                        </p>
                                    </div>
                                )}

                                {/* Action buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                                    <Button
                                        onClick={this.handleReset}
                                        className="flex items-center gap-2 bg-white text-black hover:bg-gray-200"
                                    >
                                        <RefreshCw className="w-4 h-4"/>
                                        Riprova
                                    </Button>

                                    <Button
                                        onClick={this.handleReload}
                                        variant="outline"
                                        className="border-gray-dark text-gray-light hover:bg-gray-dark hover:text-white"
                                    >
                                        Ricarica Pagina
                                    </Button>

                                    <Button
                                        onClick={() => (window.location.href = "/")}
                                        variant="outline"
                                        className="border-gray-dark text-gray-light hover:bg-gray-dark hover:text-white"
                                    >
                                        Torna alla Home
                                    </Button>
                                </div>

                                {/* Developer info (only in development) */}
                                {process.env.NODE_ENV === "development" && this.state.errorInfo && (
                                    <details className="mt-6">
                                        <summary className="cursor-pointer text-sm text-gray-medium hover:text-white">
                                            Dettagli tecnici (solo in sviluppo)
                                        </summary>
                                        <div
                                            className="mt-3 bg-black/50 border border-gray-dark rounded p-3 overflow-auto max-h-64">
                      <pre className="text-xs text-gray-light font-mono whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                                        </div>
                                    </details>
                                )}
                            </AlertDescription>
                        </Alert>
                    </div>
                </div>
            );
        }

        // No error, render children normally
        return this.props.children;
    }
}

/**
 * Functional wrapper for ErrorBoundary with hooks support
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @param {Function} props.onError - Optional error callback
 * @param {Function} props.onReset - Optional reset callback
 * @param {Function} props.fallback - Optional custom fallback UI
 * @returns {React.ReactElement} ErrorBoundary component
 */
export const ErrorBoundaryWrapper = ({children, ...props}) => {
    return <ErrorBoundary {...props}>{children}</ErrorBoundary>;
};

export default ErrorBoundary;
